import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, MessageCircle, FileText, ArrowRight, ChevronLeft } from "lucide-react";
import SEO, { JsonLd } from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import { useI18n, STR } from "../i18n";
import { PRODUCTS, CATEGORIES, COMPANY } from "../mock";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useI18n();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [mainImg, setMainImg] = useState(product?.image);

  useEffect(() => { setMainImg(product?.image); }, [slug]); // eslint-disable-line

  if (!product) {
    return (
      <section className="section" style={{ paddingTop: 160, minHeight: "60vh" }}>
        <div className="container-x" style={{ textAlign: "center" }} data-testid="product-not-found">
          <h1>{t({ es: "Producto no encontrado", en: "Product not found" })}</h1>
          <Link to="/catalogo" className="btn btn-navy" style={{ marginTop: 20 }}>← {t({ es: "Volver al catálogo", en: "Back to catalog" })}</Link>
        </div>
      </section>
    );
  }

  const cat = CATEGORIES.find((c) => c.id === product.cat);
  const related = PRODUCTS.filter((p) => p.cat === product.cat && p.slug !== slug).slice(0, 3);
  const quoteLink = `/contacto?producto=${encodeURIComponent(t(product.title))}&categoria=${encodeURIComponent(t(cat.title))}`;
  const waText = encodeURIComponent(t({ es: `Hola, me interesa cotizar: ${t(product.title)}`, en: `Hi, I'd like a quote for: ${t(product.title)}` }));

  return (
    <>
      <SEO title={product.title} path={`/catalogo/${slug}`} description={product.desc} image={product.image} type="product" />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "Product",
        name: t(product.title), image: product.gallery, description: t(product.desc),
        brand: { "@type": "Organization", name: COMPANY.name },
        category: t(cat.title),
      }} />
      <section className="section" style={{ paddingTop: 140 }}>
        <div className="container-x">
          <Breadcrumbs items={[
            { label: { es: "Inicio", en: "Home" }, href: "/" },
            { label: { es: "Catálogo", en: "Catalog" }, href: "/catalogo" },
            { label: product.title },
          ]} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 50, marginTop: 26, alignItems: "start" }}>
            <Reveal>
              <div className="card" style={{ overflow: "hidden" }}>
                <div data-testid="product-main-image" style={{ backgroundImage: `url(${mainImg})`, backgroundSize: "cover", backgroundPosition: "center", aspectRatio: "3/2" }} />
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                {product.gallery.map((g, i) => (
                  <button key={i} data-testid="product-thumb" onClick={() => setMainImg(g)} style={{ border: mainImg === g ? "2px solid var(--navy)" : "2px solid transparent", borderRadius: 8, padding: 0, cursor: "pointer", overflow: "hidden", background: "none" }}>
                    <div style={{ width: 92, height: 62, backgroundImage: `url(${g})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  </button>
                ))}
              </div>
            </Reveal>
            <Reveal delay={100}>
              <Link to={`/catalogo#${cat.id}`} className="chip" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t(cat.title)}</Link>
              <h1 data-testid="product-title" style={{ fontSize: "clamp(30px, 4vw, 44px)", margin: "14px 0 10px" }}>{t(product.title)}</h1>
              <p style={{ color: "#475066", fontSize: 16, lineHeight: 1.7 }}>{t(product.desc)}</p>
              <h3 style={{ marginTop: 26, marginBottom: 10 }}><FileText size={18} style={{ display: "inline", verticalAlign: "-3px", marginRight: 6 }} />{t({ es: "Ficha técnica", en: "Technical data" })}</h3>
              <ul data-testid="product-specs" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {t(product.specs).map((s, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", fontSize: 15, color: "var(--navy)" }}>
                    <CheckCircle2 size={17} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }} /> {s}
                  </li>
                ))}
              </ul>
              <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
                <Link to={quoteLink} data-testid="product-quote-button" className="btn btn-navy">{t(STR.cta.quote)} <ArrowRight size={15} /></Link>
                <a href={`https://wa.me/${COMPANY.phones[0].wa}?text=${waText}`} target="_blank" rel="noopener noreferrer" data-testid="product-whatsapp-button" className="btn btn-wa"><MessageCircle size={15} /> WhatsApp</a>
              </div>
              <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 14 }}>{t({ es: "Respuesta con cotización formal en menos de 24 horas.", en: "Formal quote reply within 24 hours." })}</p>
            </Reveal>
          </div>

          {related.length > 0 && (
            <div style={{ marginTop: 80 }}>
              <Reveal className="sh"><span className="eyebrow">{t({ es: "Relacionados", en: "Related" })}</span><h2>{t({ es: "También en esta línea", en: "Also in this line" })}</h2></Reveal>
              <div className="grid-3">
                {related.map((p, i) => (
                  <Reveal key={p.slug} delay={i * 60}>
                    <Link to={`/catalogo/${p.slug}`} className="card" data-testid="related-product-card" style={{ display: "block" }}>
                      <div className="img" style={{ backgroundImage: `url(${p.image})` }} />
                      <div className="body">
                        <h3 style={{ fontSize: 19 }}>{t(p.title)}</h3>
                        <p style={{ fontSize: 14 }}>{t(p.spec)}</p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          <Link to="/catalogo" className="btn btn-outline" style={{ marginTop: 50 }}><ChevronLeft size={15} /> {t({ es: "Volver al catálogo", en: "Back to catalog" })}</Link>
        </div>
      </section>
    </>
  );
}
